import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SportsEsports } from '@mui/icons-material'
import { Box, Button, Chip, FormHelperText, Skeleton, Stack, Typography } from '@mui/material'

import { GameIcon } from '../../../../../features/game'
import { Game } from '../../../../../features/game/types/Game'
import { useGetInternalGamesByIdsQuery } from '../../../../api/internal-games'
import { InternalGameSelectorDialog } from '../../../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'

export const MAX_REPORT_GAMES = 20

type ReportGamesFieldProps = {
  value: string[]
  onChange: (gameIds: string[]) => void
  disabled?: boolean
}

// Ids whose game doesn't load are kept until they are removed, so a save doesn't drop them silently.
export const ReportGamesField: FC<ReportGamesFieldProps> = ({ value, onChange, disabled }) => {
  const { t } = useTranslation()
  const [selectorOpen, setSelectorOpen] = useState(false)
  const gamesQuery = useGetInternalGamesByIdsQuery(value, { skip: value.length === 0 })
  const loadedGames = gamesQuery.data ?? []
  const [selectedGames, setSelectedGames] = useState<Game[]>([])
  const gameById = (id: string) => loadedGames.find((game) => game.id === id) ?? selectedGames.find((game) => game.id === id)
  const unloadedIds = value.filter((id) => !gameById(id))
  const missingIds = gamesQuery.isSuccess && !gamesQuery.isFetching ? unloadedIds : []
  // The kept ids count toward the limit. With room for one game the selector switches to picking a single game, which still fits.
  const selectableCount = MAX_REPORT_GAMES - unloadedIds.length
  const canSelect = selectableCount > 0 && !gamesQuery.isFetching

  const done = (games: Game[]) => {
    setSelectorOpen(false)
    setSelectedGames(games)
    onChange([...games.map(({ id }) => id), ...unloadedIds])
  }

  return (
    <Box>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        {t('internal-analysts:reports_related_games')}
      </Typography>
      {gamesQuery.isFetching && value.length > 0 ? (
        <Skeleton variant="rounded" height={32} />
      ) : (
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
          {gamesQuery.isError && (
            <Typography variant="body2" color="error">
              {t('internal-analysts:reports_related_games_load_failed', { count: value.length })}
            </Typography>
          )}
          {value.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {t('internal-analysts:reports_related_games_none')}
            </Typography>
          )}
          {value.map((id) => {
            const game = gameById(id)
            return game ? (
              <Chip
                key={id}
                avatar={<GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="xtiny" />}
                label={game.name}
                onDelete={disabled ? undefined : () => onChange(value.filter((gameId) => gameId !== id))}
              />
            ) : null
          })}
          {missingIds.length > 0 && (
            <Chip
              color="warning"
              variant="outlined"
              label={t('internal-analysts:reports_related_games_missing', { count: missingIds.length })}
              onDelete={disabled ? undefined : () => onChange(value.filter((id) => !missingIds.includes(id)))}
            />
          )}
        </Stack>
      )}
      <Button variant="outlined" startIcon={<SportsEsports />} onClick={() => setSelectorOpen(true)} disabled={disabled || !canSelect} sx={{ mt: 2 }}>
        {t('internal-analysts:reports_related_games_select')}
      </Button>
      {selectableCount <= 0 && <FormHelperText error>{t('internal-analysts:reports_related_games_limit_missing', { max: MAX_REPORT_GAMES })}</FormHelperText>}
      <InternalGameSelectorDialog
        open={selectorOpen}
        title={t('internal-analysts:reports_related_games')}
        selectedGames={value.map(gameById).filter((game): game is Game => !!game)}
        maxSelected={selectableCount}
        allowAllGames
        onClose={() => setSelectorOpen(false)}
        onDone={done}
      />
    </Box>
  )
}
